/** @jsxImportSource @emotion/react */
import Input from 'components/atoms/Input'
import Toast from 'components/atoms/Toast'
import React from 'react'
import { Button } from 'react-bootstrap'
import { skipToken } from '@reduxjs/toolkit/query'
import {
  calculateServiceLineBalances,
  deriveNextResponsible,
  getChargePayerChain,
  routePatientResponsibility,
} from 'ts-common/utils/serviceLineBalances.utils'
import { useGetBillingChargeQuery } from 'store/services/billingCharges'
import { useCreateBillingPaymentMutation } from 'store/services/billingPayments'
import {
  isWriteOffReason,
  OTHER_WRITE_OFF_REASON,
  writeOffNeedsReason,
} from 'ts-common/utils/writeOffReasons.utils'
import { ChargeAccountServiceLine, CreateBillingPaymentDto } from 'store/types'
import { billingClaimStatusSelectOptions } from 'components/lab/billingCharge/consts'
import { OrderInsuranceNotes } from 'components/lab/billingPayment/OrderInsuranceNotes'
import {
  findMalformedAdjustmentTokens,
  findUnknownAdjustmentCodes,
  parseAdjustmentCodesInput,
} from 'components/lab/billingEraReports/AdjustmentCodes'
import {
  chargeClaimNumberFor,
  defaultChainPayer,
  prefillAmounts,
  todayYyyyMmDd,
  writeOffFromAllowed,
  writeOffReasonFor,
} from './manualPostingPrefill'
import {
  DateField,
  emptyPaymentMethodDetails,
  Field,
  PAYMENT_METHOD_OPTIONS,
  PaymentMethod,
  PaymentMethodDetailsFields,
  PaymentMethodDetailValues,
  paymentMethodDetailsBody,
  validatePaymentMethodDetails,
} from './PaymentMethodDetailsFields'
import { serviceLineGrid } from './ServiceLineRow'
import { WriteOffReasonField } from './WriteOffReasonField'

// The listing row's payer mirrors — the stand-in chain while the charge
// aggregate (the only place positions 3+ live) is being fetched, and the
// gate for showing Apply Payment at all. Patient money is never posted here:
// it settles from unapplied prepayments.
export type ChargePayerChain = {
  primaryPayerId?: number | null
  primaryPayerName?: string | null
  secondaryPayerId?: number | null
  secondaryPayerName?: string | null
}

interface Props {
  patientId: number
  chargeId: string
  serviceLine: ChargeAccountServiceLine
  payerChain: ChargePayerChain
  onClose: () => void
  onDone: () => void
  // Offered when the charge has more lines than this one: an EOB that paid
  // several of them is one payment, which the Create Payment form posts with
  // every line of the charge filled in.
  onPostForCharge?: (payerId: number | null) => void
}

const toNum = (value: string | number | undefined): number => Number(value || 0) || 0

type Draft = {
  allowed: string
  paidRefund: string
  transfers: string
  patientResp: string
  writeOff: string
  writeOffReason: string
  writeOffNote: string
  // The payer's CAS codes as typed: "CO-45, PR-3".
  adjustmentCodes: string
}

// Everything about the payment itself, as opposed to the line's amounts: the
// same details the ERA and Create Payment forms record, so a payment posted
// from here can be tied to its deposit like any other.
type Header = PaymentMethodDetailValues & {
  paymentDate: string
  // Where the claim stands after this posting — the charge's claim status,
  // the same pick the ERA and Create Payment forms offer. Starts at the
  // charge's current status; blank means leave it as it is.
  claimStatus: string
  note: string
}

const prefill = (isPrimary: boolean, serviceLine: ChargeAccountServiceLine): Draft => {
  const amounts = prefillAmounts(isPrimary, serviceLine)

  return {
    allowed: amounts.allowed ? amounts.allowed.toFixed(2) : '0.00',
    paidRefund: '',
    transfers: '0.00',
    patientResp: '',
    writeOff: amounts.writeOff.toFixed(2),
    writeOffReason: amounts.writeOffReason,
    writeOffNote: '',
    adjustmentCodes: '',
  }
}

const compactField = { columnClassName: 'col-6 col-md-2', compact: true }

export const ApplyPaymentRow: React.FC<Props> = ({
  patientId,
  chargeId,
  serviceLine,
  payerChain,
  onClose,
  onDone,
  onPostForCharge,
}) => {
  const [createPayment, { isLoading: isSaving }] = useCreateBillingPaymentMutation()

  // The chain lives on the charge aggregate; the listing mirrors only carry
  // positions 1-2, so the charge is fetched when the row opens and the inputs
  // wait for it — pre-filling off the stand-in could default the wrong payer.
  const { data: chargeDetails } = useGetBillingChargeQuery(
    chargeId ? { id: chargeId, patientId: String(patientId) } : skipToken,
  )
  const isChainLoaded = String(chargeDetails?.id ?? '') === String(chargeId)
  const chain = React.useMemo(
    () => getChargePayerChain(isChainLoaded && chargeDetails ? chargeDetails : payerChain),
    [isChainLoaded, chargeDetails, payerChain],
  )

  const [payerId, setPayerId] = React.useState<number | null>(null)
  const [showReasonError, setShowReasonError] = React.useState(false)
  const [draft, setDraft] = React.useState<Draft>(() => prefill(true, serviceLine))
  const [header, setHeader] = React.useState<Header>(() => ({
    paymentMethod: 'Check',
    ...emptyPaymentMethodDetails(),
    paymentDate: todayYyyyMmDd(),
    claimStatus: '',
    note: '',
  }))

  // Default the picker once the real chain is in; the biller's own pick wins.
  React.useEffect(() => {
    if (isChainLoaded && payerId === null) {
      const entry = defaultChainPayer(chain, [serviceLine])

      if (entry) {
        setPayerId(Number(entry.payerId))
        setDraft(prefill(Number(entry.position) === 1, serviceLine))
      }
    }
  }, [isChainLoaded, payerId, chain, serviceLine])

  // The claim status the charge carries now, filled in once the charge is
  // in so the biller only moves it when the posting changes where the claim
  // stands. A pick already made is kept.
  const chargeClaimStatus = chargeDetails?.claimStatus ?? ''

  React.useEffect(() => {
    if (isChainLoaded) {
      setHeader((current) => (
        current.claimStatus ? current : { ...current, claimStatus: chargeClaimStatus }
      ))
    }
  }, [isChainLoaded, chargeClaimStatus])

  const selectedEntry = chain.find((entry) => Number(entry.payerId) === Number(payerId))
  const position = Number(selectedEntry?.position) || 1
  const isPrimary = position === 1

  const nextResponsible = deriveNextResponsible({
    paymentByPayerId: payerId ?? undefined,
    payers: chain,
  })

  const selectPayer = (id: number) => {
    const entry = chain.find((candidate) => Number(candidate.payerId) === id)

    setPayerId(id)
    setDraft(prefill(Number(entry?.position) === 1, serviceLine))
  }

  const setField = (field: keyof Draft) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target

    setDraft((current) => {
      const next = { ...current, [field]: value }

      // The payer-stated PR rides plan-side to the next payer when one
      // follows — mirror it into the transfer column as it is typed.
      if (field === 'patientResp') {
        next.transfers = routePatientResponsibility(toNum(value), nextResponsible).transfers.toFixed(2)
      }

      if (field === 'allowed') {
        const writeOff = writeOffFromAllowed(prefillAmounts(isPrimary, serviceLine).baseCharged, value)

        next.writeOff = writeOff.toFixed(2)
        next.writeOffReason = writeOffReasonFor(writeOff, current.writeOffReason)
      }

      // Clearing the write-off clears its reason with it, so a row edited back
      // to $0 never carries a stale "why".
      if ((field === 'writeOff' || field === 'allowed') && !writeOffNeedsReason(toNum(next.writeOff))) {
        next.writeOffReason = ''
        next.writeOffNote = ''
      }

      return next
    })
  }

  const setHeaderField = (field: string, value: string) =>
    setHeader((current) => ({ ...current, [field]: value }))

  // Two ways a typed code is wrong: it is not shaped like one, or it names a
  // group or CARC that does not exist. Both are shown as they are typed and
  // both refuse the save — a code the payer never issued explains nothing.
  const malformedCodes = findMalformedAdjustmentTokens(draft.adjustmentCodes)
  const unknownCodes = findUnknownAdjustmentCodes(draft.adjustmentCodes)
  const adjustmentCodesInvalid = malformedCodes.length > 0 || unknownCodes.length > 0

  const writeOffReasonMissing =
    writeOffNeedsReason(toNum(draft.writeOff))
    && (!isWriteOffReason(draft.writeOffReason)
      || (draft.writeOffReason === OTHER_WRITE_OFF_REASON && !draft.writeOffNote.trim()))

  const save = async () => {
    if (payerId === null) {
      return
    }

    // A write-off reduces what anyone can still collect, so it does not go in
    // unexplained. The server enforces the same rule.
    if (writeOffReasonMissing) {
      setShowReasonError(true)
      return
    }

    if (adjustmentCodesInvalid) {
      return
    }

    if (!header.paymentDate) {
      Toast({ type: 'error', label: 'Payment date is required' })
      return
    }

    const detailsError = validatePaymentMethodDetails(header)

    if (detailsError) {
      Toast({ type: 'error', label: detailsError })
      return
    }

    const payerName = selectedEntry?.payerName || 'Unknown payer'
    const { baseCharged } = prefillAmounts(isPrimary, serviceLine)
    const routed = routePatientResponsibility(toNum(draft.patientResp), nextResponsible)
    const paymentDate = new Date(`${header.paymentDate}T00:00:00`).toISOString()
    const adjustments = parseAdjustmentCodesInput(draft.adjustmentCodes)
    // The control number this charge was submitted under to the paying
    // payer — the claim number an ERA for it would carry, so a hand posting
    // is recorded under the same one.
    const claimNumber = chargeClaimNumberFor(chargeDetails, position)
    const note = header.note.trim()
    // Only a status the biller actually moved is written back to the charge;
    // a posting that leaves it where it was must not touch it.
    const claimStatusChanged = header.claimStatus !== '' && header.claimStatus !== chargeClaimStatus

    const balances = calculateServiceLineBalances({
      charged: baseCharged,
      allowed: toNum(draft.allowed),
      paidRefund: toNum(draft.paidRefund),
      transfers: toNum(draft.transfers),
      copay: routed.copay,
      writeOff: toNum(draft.writeOff),
    })

    const amounts = {
      allowed: balances.allowed,
      paidRefund: balances.paidRefund,
      transfers: balances.transfers,
      copay: balances.copay,
      writeOff: balances.writeOff,
    }

    try {
      const payload = {
        patientId,
        chargeId,
        paymentByPayerId: payerId,
        nextResponsiblePayerId: nextResponsible.type === 'PAYER' ? nextResponsible.payerId : null,
        nextResponsiblePatientId: nextResponsible.type === 'PATIENT' ? patientId : null,
        paymentMethod: header.paymentMethod,
        ...paymentMethodDetailsBody(header),
        paymentDate,
        appointmentDate: serviceLine.startDos || paymentDate,
        notes: note ? [note] : [],
        status: 'APPLIED',
        claimNumber,
        ...(claimStatusChanged ? { claimStatus: header.claimStatus } : {}),
        serviceData: [{
          srNo: 1,
          cptCode: serviceLine.cptCode,
          startDateOfService: serviceLine.startDos || undefined,
          charged: baseCharged,
          balance: balances.charged,
          ...amounts,
          planBalance: balances.planBalance,
          patientBalance: balances.patientBalance,
          transactions: [{
            date: paymentDate,
            txId: claimNumber,
            paymentBy: payerName,
            ...amounts,
            writeOffReason: draft.writeOffReason || null,
            writeOffNote: draft.writeOffNote.trim() || undefined,
            ...(adjustments.length ? { adjustments } : {}),
          }],
        }],
      }

      await createPayment(payload as unknown as CreateBillingPaymentDto).unwrap()

      Toast({ type: 'success', label: 'Payment applied' })
      onDone()
      onClose()
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message || 'Failed to apply payment'
      Toast({ type: 'error', label: errorMessage })
    }
  }

  const moneyInput = (field: keyof Draft) => (
    <Input
      type="text"
      className="form-control form-control-sm"
      value={draft[field]}
      disabled={isSaving}
      handleChange={setField(field)}
      css={{ backgroundColor: 'white', border: '1px solid #ced4da' }}
    />
  )

  const rowStyle = {
    ...serviceLineGrid,
    // Top-aligned, unlike the listing rows: the payer cell is two lines tall
    // and the amount inputs belong level with its select, not its middle.
    alignItems: 'start',
    padding: '0.75rem 0',
    backgroundColor: '#F3F9F3',
    borderBottom: '1px solid #bbbbbb',
  }

  if (!isChainLoaded || payerId === null) {
    return (
      <div css={rowStyle}>
        <div />
        <div css={{ gridColumn: 'span 12', color: '#6c757d', fontSize: '0.875rem' }}>
          Loading the charge's payer chain…
        </div>
      </div>
    )
  }

  return (
    <div css={rowStyle}>
      {/* Row 1 — the line: who paid and the amounts, under their columns. */}
      <div />
      <div css={{ gridColumn: 'span 5', fontSize: '0.875rem', paddingRight: '1rem' }}>
        <div css={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <select
            className="form-control form-control-sm"
            value={payerId}
            disabled={isSaving}
            onChange={(event) => selectPayer(Number(event.target.value))}
            css={{ flex: '1 1 50%', minWidth: 0 }}>
            {chain.map((entry) => (
              <option key={String(entry.payerId)} value={Number(entry.payerId)}>
                {entry.position}. {entry.payerName || 'Unknown payer'}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Adj. codes - CO-45, PR-3"
            value={draft.adjustmentCodes}
            disabled={isSaving}
            onChange={(event) => setDraft((current) => ({
              ...current,
              adjustmentCodes: event.target.value,
            }))}
            css={{
              flex: '1 1 50%',
              minWidth: 0,
              borderColor: adjustmentCodesInvalid ? '#dc3545' : undefined,
            }}
          />
        </div>
        <div css={{ color: '#6c757d', fontSize: '0.8rem', marginTop: '0.25rem' }}>
          Next responsible:{' '}
          {nextResponsible.type === 'PAYER'
            ? `${nextResponsible.payerName || 'Next payer'} - PR transfers to payer`
            : 'Patient - PR bills the patient'}
        </div>
        {malformedCodes.length > 0 && (
          <div className="text-danger" css={{ fontSize: '0.8rem', marginTop: '0.15rem' }}>
            Not a code: {malformedCodes.join(', ')} - use GROUP-CODE, e.g. CO-45
          </div>
        )}
        {unknownCodes.length > 0 && (
          <div className="text-danger" css={{ fontSize: '0.8rem', marginTop: '0.15rem' }}>
            Unknown code: {unknownCodes.join(', ')}
          </div>
        )}
        {onPostForCharge && (
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            css={{ fontSize: '0.8rem', marginTop: '0.25rem' }}
            disabled={isSaving}
            onClick={(event) => {
              event.stopPropagation()
              onPostForCharge(payerId)
            }}>
            EOB paid several lines? Post one payment for the whole charge
          </button>
        )}
      </div>
      <div>{moneyInput('allowed')}</div>
      <div>{moneyInput('paidRefund')}</div>
      <div>{moneyInput('transfers')}</div>
      <div>{moneyInput('patientResp')}</div>
      <div>
        {moneyInput('writeOff')}
        <WriteOffReasonField
          writeOff={draft.writeOff}
          reason={draft.writeOffReason}
          note={draft.writeOffNote}
          disabled={isSaving}
          hasError={showReasonError && writeOffReasonMissing}
          onReasonChange={(value) => setDraft((current) => ({ ...current, writeOffReason: value }))}
          onNoteChange={(value) => setDraft((current) => ({ ...current, writeOffNote: value }))}
        />
      </div>
      <div
        className="d-flex justify-content-end align-items-center"
        css={{ gridColumn: 'span 3', flexWrap: 'nowrap', gap: '0.5rem', paddingRight: '0.5rem' }}>
        <Button
          variant="primary"
          size="sm"
          disabled={isSaving || adjustmentCodesInvalid}
          onClick={(e) => {
            e.stopPropagation()
            save()
          }}
          css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={isSaving}
          onClick={(e) => {
            e.stopPropagation()
            onClose()
          }}
          css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}>
          Cancel
        </Button>
      </div>

      {/* Row 2 — the payment: where the claim stands after it and what the
          EOB says about the money itself. It spans every column past the
          gutter so the amount inputs above keep sitting exactly under the
          balance columns they belong to. */}
      <div />
      <div css={{ gridColumn: '2 / -1', paddingRight: '0.5rem', marginTop: '0.75rem' }}>
        <div className="row" css={{ rowGap: '0.35rem' }}>
          <Field {...compactField} label="Claim status">
            <select
              className="form-control form-control-sm"
              value={header.claimStatus}
              disabled={isSaving}
              onChange={(event) => setHeaderField('claimStatus', event.target.value)}>
              <option value="">Select</option>
              {billingClaimStatusSelectOptions.map((option) => (
                <option key={option.key} value={option.key}>{option.value}</option>
              ))}
            </select>
          </Field>
          <DateField
            {...compactField}
            label="Payment date"
            value={header.paymentDate}
            disabled={isSaving}
            onChange={(value) => setHeaderField('paymentDate', value)}
          />
          <Field {...compactField} label="Payment method">
            <select
              className="form-control form-control-sm"
              value={header.paymentMethod}
              disabled={isSaving}
              onChange={(event) => setHeaderField('paymentMethod', event.target.value as PaymentMethod)}>
              {PAYMENT_METHOD_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>{option.value}</option>
              ))}
            </select>
          </Field>
          <PaymentMethodDetailsFields
            {...compactField}
            values={header}
            setFieldValue={setHeaderField}
            disabled={isSaving}
          />
          <Field columnClassName="col-12 col-md" compact label="Note">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Optional note"
              value={header.note}
              disabled={isSaving}
              onChange={(event) => setHeaderField('note', event.target.value)}
            />
          </Field>
        </div>
        <OrderInsuranceNotes patientId={patientId} chargeId={chargeId} compact />
      </div>

    </div>
  )
}
